import {useEffect, useMemo, useState} from 'react';

import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

import type {UseQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type {QueryDefinition} from '@reduxjs/toolkit/query/react';
import type {ListResponseType} from 'sweet-types';

export type UseQueryType<FP, IT> = UseQuery<
  QueryDefinition<FP, any, any, ListResponseType<IT>, 'sweetAPI'>
>;

export type InfiniteListFetcherHookProps<
  IT extends {id: number | string},
  FP extends {page: string}
> = {
  page?: number;
  pageParam?: string;
  fetchParams: Record<string, any>;
  useDataHook: UseQueryType<FP, IT>;
  paramsConverter: (query: Record<string, any>) => FP;
};

export function useInfiniteListFetcherRTK<
  IT extends {id: number | string},
  FP extends {page: string}
>({
  page = 1,
  pageParam,
  fetchParams,
  useDataHook,
  paramsConverter,
}: InfiniteListFetcherHookProps<IT, FP>) {
  // States
  const [fingerPrints, setFingerPrints] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(page);
  const [paginatedResults, setPaginatedResults] = useState<
    Record<string, Array<IT>>
  >({});
  const [allResults, setAllResults] = useState<Array<IT>>([]);
  const [params, setParams] = useState<Record<string, any>>({});

  const buildParamsArray = (fetchParams, page: number) =>
    paramsConverter({
      ...(fetchParams || {}),
      [pageParam || 'page']: page.toString(),
    });

  const isParamsChanging = !isEqual(fetchParams, params);

  const current = useDataHook(buildParamsArray(params, currentPage), {
    skip: isParamsChanging,
    refetchOnMountOrArgChange: 60,
  });

  useEffect(() => {
    if (!current.isFetching && current.data?.results) {
      const [fingerPrint] = getFingerPrints([
        ...(allResults ?? []), // previous results
        ...(current.data?.results ?? []), // new results
      ]);

      setFingerPrints(fingerPrint);

      setPaginatedResults((prevResults) => {
        const updated = {...prevResults};
        updated[currentPage] = current.data?.results ?? [];

        return updated;
      });
    }
  }, [current.isFetching, currentPage, params]);

  // reset the currentPage when params change, prevent searching on page 2 accidentally
  useEffect(() => {
    setAllResults([]);
    setPaginatedResults({});
    setParams(fetchParams);
    setCurrentPage(page);
  }, [JSON.stringify(fetchParams)]);

  const loadNext = () => setCurrentPage((currentPage) => currentPage + 1);

  const getFingerPrints = (responses: Array<IT>): [number] => {
    let fingerprint = 0;
    responses.map((item, index) => {
      fingerprint += index;
    });
    return [fingerprint];
  };

  const results: Array<IT> = useMemo(
    () => uniqBy(Object.values(paginatedResults).flat(), (item) => item.id),
    [paginatedResults]
  );
  return {
    results,
    resultsFingerprint: fingerPrints,
    loadNext,
    hasNext: current.data?.next !== null,
    isLoading: current.isLoading,
    isFetching: current.isFetching,
    error: current.error,
    totalCount: current.data?.count || 0,
    refetch: current.refetch, // we need refetch system now. but will remove this asap
  };
}
